import { useCallback, useEffect, useRef, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { GeneratePresentationCustomSlidePromptInput } from '@/types/schema';

import { useGenerateCustomSlideContentSubscription } from '../graphql/GenerateCustomSlideContent.generated';

export type GenerationStatus = 'idle' | 'generating' | 'error';

export interface UseGenerateCustomSlideContentOptions {
  /** Called with the final complete text when generation succeeds */
  onComplete: (generatedContent: string) => void;
}

export interface UseGenerateCustomSlideContentReturn {
  /** Kick off generation with the given parameters */
  startGeneration: (input: GeneratePresentationCustomSlidePromptInput) => void;
  /** Cancel an in-progress generation and restore idle state */
  cancelGeneration: () => void;
  /** Current status of the generation */
  status: GenerationStatus;
  /** The accumulated text so far (for display during streaming) */
  streamedContent: string;
}

/**
 * Display refresh interval for streaming content (ms).
 * We accumulate text in a ref and flush to state on this interval
 * to avoid re-rendering the Markdown preview on every tiny chunk.
 */
const DISPLAY_REFRESH_INTERVAL_MS = 150;

const EMPTY_INPUT: GeneratePresentationCustomSlidePromptInput = {
  presentationID: '',
  prompt: '',
};

/**
 * Hook that manages streaming AI content generation for custom slide fields.
 *
 * Uses a ref-based accumulation pattern (matching DocumentChatProvider) to
 * decouple streaming frequency from render frequency. Text chunks are
 * accumulated in a ref and flushed to display state every 150ms.
 */
export function useGenerateCustomSlideContent({
  onComplete,
}: UseGenerateCustomSlideContentOptions): UseGenerateCustomSlideContentReturn {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const [generationInput, setGenerationInput] =
    useState<GeneratePresentationCustomSlidePromptInput | null>(null);
  const [streamedContent, setStreamedContent] = useState('');
  const [status, setStatus] = useState<GenerationStatus>('idle');

  // Accumulate text in a ref to avoid stale closures in subscription callbacks
  const accumulatedTextRef = useRef('');
  const displayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  // Ref to the latest onComplete to avoid stale closure in subscription callbacks
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const stopDisplayRefresh = useCallback(() => {
    if (displayIntervalRef.current) {
      clearInterval(displayIntervalRef.current);
      displayIntervalRef.current = null;
    }
    // Final flush to ensure display is up to date
    setStreamedContent(accumulatedTextRef.current);
  }, []);

  const startDisplayRefresh = useCallback(() => {
    if (displayIntervalRef.current) return;
    displayIntervalRef.current = setInterval(() => {
      setStreamedContent(accumulatedTextRef.current);
    }, DISPLAY_REFRESH_INTERVAL_MS);
  }, []);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (displayIntervalRef.current) {
        clearInterval(displayIntervalRef.current);
      }
    };
  }, []);

  const reset = useCallback(() => {
    setGenerationInput(null);
    setStreamedContent('');
    setStatus('idle');
    accumulatedTextRef.current = '';
    stopDisplayRefresh();
  }, [stopDisplayRefresh]);

  const cancelGeneration = useCallback(() => {
    reset();
  }, [reset]);

  useGenerateCustomSlideContentSubscription({
    fetchPolicy: 'no-cache',
    variables: { input: generationInput ?? EMPTY_INPUT },
    skip: !generationInput,
    onData: ({ data }) => {
      const result = data.data?.generatePresentationCustomSlidePrompt;
      if (!result) return;

      if (result.error) {
        stopDisplayRefresh();
        setStatus('error');
        setGenerationInput(null);
        showFeedback('Failed to generate content. Please try again.');
        reportError('Custom slide generation error', new Error(result.error));
        return;
      }

      if (result.textDelta) {
        accumulatedTextRef.current += result.textDelta;
      }

      if (result.isComplete) {
        // Prefer generatedContent (full text) from the terminal event,
        // falling back to the accumulated chunks.
        const finalText = result.generatedContent || accumulatedTextRef.current;
        stopDisplayRefresh();
        reset();
        if (finalText.trim()) {
          onCompleteRef.current(finalText);
        }
      }
    },
    onError: (err) => {
      stopDisplayRefresh();
      setStatus('error');
      setGenerationInput(null);
      showFeedback('Failed to generate content. Please try again.');
      reportError('Custom slide content generation failed', err);
    },
  });

  const startGeneration = useCallback(
    (input: GeneratePresentationCustomSlidePromptInput) => {
      accumulatedTextRef.current = '';
      setStreamedContent('');
      setStatus('generating');
      setGenerationInput(input);
      startDisplayRefresh();
    },
    [startDisplayRefresh]
  );

  return {
    startGeneration,
    cancelGeneration,
    status,
    streamedContent,
  };
}
